import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Wind, Sun, Activity, ArrowRight } from "lucide-react";

// Curated CDN images (stable, no Unsplash decay)
const IMG_TAPIS_BAMBOU =
  "https://static.prod-images.emergentagent.com/jobs/981795dc-3f85-41a2-8218-f3db7d35fb62/images/9936be25bf8187b244297508bf0b8587d4d3acedbacb8db9d5c0a2cf0811f187.png";
const IMG_HALO_LAMP =
  "https://static.prod-images.emergentagent.com/jobs/981795dc-3f85-41a2-8218-f3db7d35fb62/images/b052b066ad5179f02b15b4e7ff929d23ee44a5e9d400e21ecc554d172630fb65.png";
const IMG_OXYGEN =
  "https://static.prod-images.emergentagent.com/jobs/981795dc-3f85-41a2-8218-f3db7d35fb62/images/6b41f8d545ad44f055926491aad383b1c6de6b1bce2fcc6b12988214133501c5.png";
const IMG_CLOUD =
  "https://static.prod-images.emergentagent.com/jobs/981795dc-3f85-41a2-8218-f3db7d35fb62/images/6506d52b1fee6d2964d6f14a538206a5ca23511f75c5a9506d6c5b467cc5e95a.png";
const IMG_SUNRISE =
  "https://static.prod-images.emergentagent.com/jobs/981795dc-3f85-41a2-8218-f3db7d35fb62/images/8c7287cad94860cfc9dec1409550e7f54fd2cca7181a9e884c979154943ae4f2.png";

export default function About() {
  const { t } = useTranslation();
  return (
    <div data-testid="about-page" className="bg-[#001F3F] text-white">
      {/* ────────── HERO ────────── */}
      <section className="relative overflow-hidden border-b border-white/10">
        <div className="max-w-6xl mx-auto px-6 md:px-12 pt-24 pb-32 grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-7">
            <div
              className="text-xs font-bold uppercase tracking-[0.3em] text-[#C5CDD8]"
              data-testid="about-eyebrow"
            >
              {t("about.eyebrow")} · Édition 2026
            </div>
            <h1
              className="font-display mt-6 text-5xl sm:text-6xl lg:text-7xl font-bold uppercase tracking-tighter leading-[0.88] text-white"
              data-testid="about-title"
            >
              Le bureau,<br />
              <span className="silver-shimmer">repensé</span><br />
              en un acte<br />
              de sérénité.
            </h1>
            <p className="mt-10 max-w-xl text-base sm:text-lg leading-relaxed text-white/75">
              Nous passons en moyenne <strong className="text-white">90 000 heures</strong> de
              notre vie au bureau. Ce lieu, devenu second foyer, mérite mieux qu'un
              écran et une chaise. Il mérite l'attention, le soin, l'élégance — la
              promesse silencieuse d'un travail qui n'épuise pas.
            </p>
          </div>
          <div className="md:col-span-5">
            <div className="relative aspect-[4/5] overflow-hidden border border-white/10">
              <img
                src={IMG_TAPIS_BAMBOU}
                alt="Bureau minimaliste — Nord Stella Tech"
                className="w-full h-full object-cover"
                data-testid="about-hero-image"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-[#001F3F]/60 via-transparent to-transparent" />
            </div>
          </div>
        </div>
      </section>

      {/* ────────── 1. NOTRE CONVICTION ────────── */}
      <section className="border-b border-white/10" data-testid="about-conviction">
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-28 grid md:grid-cols-12 gap-16 items-center">
          <div className="md:col-span-5 order-2 md:order-1">
            <div className="relative aspect-square overflow-hidden border border-white/10">
              <img
                src={IMG_CLOUD}
                alt="Détail texture — produit Nord Stella"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
          <div className="md:col-span-7 order-1 md:order-2">
            <div className="text-xs font-bold uppercase tracking-[0.3em] text-[#C5CDD8]">
              I. Notre conviction
            </div>
            <h2 className="font-display mt-6 text-3xl sm:text-4xl lg:text-5xl font-bold uppercase tracking-tighter leading-[0.95] text-white">
              Le bureau moderne doit être<br />
              <span className="silver-shimmer">un espace de sérénité.</span>
            </h2>
            <p className="mt-8 text-base sm:text-lg leading-relaxed text-white/75">
              Nord Stella Tech est né d'une conviction simple : la performance ne
              s'oppose pas à la douceur. La concentration ne s'achète pas dans
              l'épuisement. Et l'innovation, lorsqu'elle est bien pensée, n'agresse
              jamais — elle apaise.
            </p>
            <p className="mt-6 text-base sm:text-lg leading-relaxed text-white/65">
              Nous avons fondé cette maison pour ceux qui refusent l'arbitrage
              entre santé et productivité, entre design et fonction, entre
              technologie et calme. Pour ceux qui veulent travailler longtemps,
              bien, et en accord avec eux-mêmes.
            </p>
          </div>
        </div>
      </section>

      {/* ────────── 2. DESIGN + SMART TECH 2026 ────────── */}
      <section className="bg-[#000B1A] border-b border-white/10" data-testid="about-collection">
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-28 grid md:grid-cols-12 gap-16 items-center">
          <div className="md:col-span-7">
            <div className="text-xs font-bold uppercase tracking-[0.3em] text-[#C5CDD8]">
              II. Sélection 2026
            </div>
            <h2 className="font-display mt-6 text-3xl sm:text-4xl lg:text-5xl font-bold uppercase tracking-tighter leading-[0.95] text-white">
              L'élégance du Nord,<br />
              <span className="silver-shimmer">l'intelligence du futur.</span>
            </h2>
            <p className="mt-8 text-base sm:text-lg leading-relaxed text-white/75">
              Notre collection 2026 marie deux héritages que tout semblait opposer :
              le minimalisme rigoureux du <strong className="text-white">design
              nordique</strong> — bois clair, lignes pures, matières honnêtes — et
              l'audace technologique des objets connectés les plus avancés.
            </p>
            <p className="mt-6 text-base sm:text-lg leading-relaxed text-white/65">
              Lampes à cycle circadien, diffuseurs à lévitation magnétique,
              capteurs d'air en temps réel, charge sans-fil multi-appareils :
              chaque objet est choisi pour disparaître dans votre quotidien — tout
              en élevant chaque geste qui le compose.
            </p>
            <div className="mt-10 flex flex-wrap gap-x-12 gap-y-6 text-xs uppercase tracking-[0.25em] text-white/50 font-bold">
              <span>↑ Smart Tech</span>
              <span>↑ Wellness</span>
              <span>↑ Ergonomie</span>
              <span>↑ Air & Lumière</span>
            </div>
          </div>
          <div className="md:col-span-5">
            <div className="grid grid-cols-2 gap-4">
              <div className="aspect-[3/4] overflow-hidden border border-white/10">
                <img src={IMG_HALO_LAMP} alt="Lampe LED Halo Circadian" className="w-full h-full object-cover" />
              </div>
              <div className="aspect-[3/4] overflow-hidden border border-white/10 mt-12">
                <img src={IMG_SUNRISE} alt="Réveil Halo Sunrise" className="w-full h-full object-cover" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ────────── 3. NOS 3 ENGAGEMENTS — Productivité saine ────────── */}
      <section className="border-b border-white/10" data-testid="about-pillars">
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-28">
          <div className="text-xs font-bold uppercase tracking-[0.3em] text-[#C5CDD8]">
            III. Notre engagement
          </div>
          <h2 className="font-display mt-6 max-w-3xl text-3xl sm:text-4xl lg:text-5xl font-bold uppercase tracking-tighter leading-[0.95] text-white">
            Une productivité qui<br />
            <span className="silver-shimmer">ne coûte pas votre santé.</span>
          </h2>
          <p className="mt-8 max-w-2xl text-base sm:text-lg leading-relaxed text-white/65">
            Travailler longtemps suppose de travailler mieux. Trois priorités
            non-négociables guident notre catalogue :
          </p>

          <div className="mt-16 grid md:grid-cols-3 gap-px bg-white/10">
            {[
              {
                icon: Activity,
                num: "01",
                title: "Ergonomie",
                desc: "Tapis bambou large, supports d'écran aluminium, repose-poignets ergonomiques — la posture juste pour les longues heures. Validés par des kinésithérapeutes.",
                testid: "pillar-ergonomie",
              },
              {
                icon: Sun,
                num: "02",
                title: "Lumière douce",
                desc: "Lampes circadiennes 2700K→6500K, réveils à simulation d'aube, écrans à intensité auto. Respect du rythme biologique de la matinée au soir.",
                testid: "pillar-lumiere",
              },
              {
                icon: Wind,
                num: "03",
                title: "Air pur",
                desc: "Purificateurs HEPA, diffuseurs ultrasoniques, plantes auto-arrosantes connectées. L'air que vous respirez 8h par jour ne devrait jamais être un compromis.",
                testid: "pillar-air",
              },
            ].map((p) => (
              <div
                key={p.num}
                className="bg-[#001F3F] p-10 lg:p-12"
                data-testid={p.testid}
              >
                <div className="flex items-center justify-between">
                  <p.icon size={28} strokeWidth={1.25} className="text-[#FFD700]" />
                  <span className="text-xs font-bold tracking-[0.25em] text-white/30">
                    {p.num}
                  </span>
                </div>
                <h3 className="font-display mt-10 text-2xl font-bold uppercase tracking-tight text-white">
                  {p.title}
                </h3>
                <p className="mt-4 text-sm leading-relaxed text-white/65">
                  {p.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ────────── VISION QUOTE ────────── */}
      <section className="relative overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 opacity-20">
          <img
            src={IMG_OXYGEN}
            alt=""
            className="w-full h-full object-cover blur-2xl"
            aria-hidden="true"
          />
        </div>
        <div className="relative max-w-4xl mx-auto px-6 md:px-12 py-32 text-center">
          <div className="text-xs font-bold uppercase tracking-[0.3em] text-[#C5CDD8]">
            Notre vision
          </div>
          <p className="font-display mt-8 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter uppercase leading-[1.05] text-white">
            « Un jour, le bureau ne sera plus<br />
            un lieu où l'on <span className="line-through opacity-50">s'épuise</span>.<br />
            Il sera un lieu où l'on{" "}
            <span className="silver-shimmer">s'épanouit</span>. »
          </p>
          <p className="mt-10 text-xs font-bold uppercase tracking-[0.3em] text-white/60">
            — L'équipe Nord Stella Tech, Lille · 2026
          </p>
        </div>
      </section>

      {/* ────────── CTA ────────── */}
      <section className="max-w-6xl mx-auto px-6 md:px-12 py-28 text-center">
        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold uppercase tracking-tighter leading-[0.95] text-white">
          Prêt à transformer<br />
          votre espace de travail ?
        </h2>
        <p className="mt-6 max-w-xl mx-auto text-white/65">
          Découvrez notre sélection 2026 — livraison soignée sous 4 à 7 jours,
          garantie satisfait ou remboursé sous 30 jours.
        </p>
        <Link
          to="/shop"
          className="mt-12 inline-flex items-center gap-3 border border-white bg-white text-[#001F3F] hover:bg-[#FFD700] hover:border-[#FFD700] hover:text-[#001F3F] transition-all uppercase tracking-[0.25em] text-xs font-bold px-10 py-5"
          data-testid="about-cta"
        >
          Découvrir le catalogue
          <ArrowRight size={14} strokeWidth={2} />
        </Link>
      </section>
    </div>
  );
}
